/**
 * ConstructorCollectionBrowser — Collection Browser Module
 * Extracted from constructor-listing.js
 *
 * This class extends ConstructorListingBase and handles collection browsing
 * with facets, pagination, and filter state management.
 */

/* ═══════════════════════════════════════════════════════════════
   CLASS — ConstructorCollectionBrowser (extends base)
   ═══════════════════════════════════════════════════════════════ */
class ConstructorCollectionBrowser extends ConstructorListingBase {
    constructor() {
        super();
        this.state.collectionHandle = '';
    }

    getPageType() {
        return 'collection';
    }

    connectedCallback() {
        const tpl = this.querySelector('template[id^="constructor-collection-card"]');
        if (tpl?.content) {
            const div = document.createElement('div');
            div.appendChild(tpl.content.cloneNode(true));
            this.cardTemplate = div.innerHTML.trim();
        }

        this.initStateFromUrl();

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                observer.unobserve(this);
                this.initialize();
            },
            { rootMargin: '0px 0px 400px 0px' }
        );

        observer.observe(this);
    }

    initStateFromUrl() {
        const params = new URLSearchParams(window.location.search);
        this.state.collectionHandle = this.dataset.collectionHandle || '';
        this.state.page = Number(params.get('page')) || 1;
        this.state.sortBy = params.get('sort_by') || '';
        this.state.sortOrder = params.get('sort_order') || 'ascending';
        this.state.filters = {};

        params.forEach((val, key) => {
            const match = key.match(/^filters\[(.+)\]$/);
            if (match) {
                const facet = match[1];
                if (!this.state.filters[facet]) this.state.filters[facet] = new Set();
                this.state.filters[facet].add(val);
            }
        });
    }

    buildApiUrl(params) {
        return (
            'https://ac.cnstrc.com/browse/group_id/' +
            encodeURIComponent(this.state.collectionHandle) +
            '?' +
            params.toString()
        );
    }

    async fetchData(page = 1, filters = {}) {
        const params = new URLSearchParams({
            key: CONSTRUCTOR_CONFIG.API_KEY,
            section: 'Products',
            num_results_per_page:
                parseInt(this.dataset.resultsPerPage) || CONSTRUCTOR_CONFIG.DEFAULT_RESULTS,
            page: page,
            c: 'cio-shopify-theme-1.0'
        });
        this._addIdentityParams(params);

        if (this.state.sortBy) params.set('sort_by', this.state.sortBy);
        if (this.state.sortOrder) params.set('sort_order', this.state.sortOrder);

        Object.entries(filters).forEach(([facet, values]) => {
            values.forEach((val) => params.append('filters[' + facet + ']', val));
        });

        const url = this.buildApiUrl(params);
        const res = await fetch(url, { headers: { Accept: 'application/json' } });

        if (!res.ok) throw new Error('Constructor API error: ' + res.status);
        return await res.json();
    }

    async fetchUnfilteredCounts() {
        const params = new URLSearchParams({
            key: CONSTRUCTOR_CONFIG.API_KEY,
            section: 'Products',
            num_results_per_page: 1,
            page: 1,
            c: 'cio-shopify-theme-1.0'
        });
        this._addIdentityParams(params);

        const url =
            'https://ac.cnstrc.com/browse/group_id/' +
            encodeURIComponent(this.state.collectionHandle) +
            '?' +
            params.toString();
        const res = await fetch(url, { headers: { Accept: 'application/json' } });

        if (!res.ok) throw new Error('Constructor API error: ' + res.status);
        return await res.json();
    }

    reInitAlertBar() {
        const alertBar = this.querySelector('.product-grid-container__alert');
        const storeWay = localStorage.getItem('storeWay') || 'all';
        const storeLocation = localStorage.getItem('storeLocation') || null;

        if (!alertBar) return;
        alertBar.classList.add('hidden');
        alertBar.removeAttribute('data-way');

        if (!storeLocation && storeWay !== 'online') return;

        const currentUrl = new URL(window.location.href);
        const hasPickupFilter = currentUrl.searchParams.has('filters[pickup_in_store]');
        const hasViewFilter = currentUrl.searchParams.has('filters[view_in_store]');
        const hasShippableFilter = currentUrl.searchParams.has(
            'filters[shippable_quantity_boolean]'
        );

        if (!hasPickupFilter || !hasViewFilter || !hasShippableFilter) return;

        alertBar.setAttribute('data-way', storeWay);

        const currentStoreEl = alertBar.querySelector('[data-current-store]');
        if (currentStoreEl) currentStoreEl.textContent = storeLocation || '';

        const editButton = alertBar.querySelector('button');
        if (editButton) {
            const newButton = editButton.cloneNode(true);
            editButton.parentNode.replaceChild(newButton, editButton);

            newButton.addEventListener('click', (event) => {
                event.preventDefault();
                const modalOpener = document.querySelector('[data-store-modal-opener]');
                if (modalOpener) {
                    const button = modalOpener.querySelector('button');
                    if (button) button.click();
                }
            });
        }

        alertBar.classList.remove('hidden');
    }

    updateMobileProductCount() {
        const counter = this.querySelector('.mobile-facets__count');
        if (counter) {
            const count = this.state.totalNumResults;
            counter.textContent = count + ' of ' + this.state.totalNumResults + ' products';
        }
    }

    reInitMobileYourStoreFacet() {
        const mobileFacet = this.querySelector('your-store-facet-cio.facet-store-selector--mobile');
        const desktopFacet = this.querySelector('your-store-facet-cio');

        if (!mobileFacet) return;

        const way = localStorage.getItem('storeWay') || 'all';
        const store = localStorage.getItem('storeLocation') || null;
        const availability = localStorage.getItem('storeAvailability') || null;

        const mobileWayRadios = mobileFacet.querySelectorAll('input[name="facet-way"]');
        mobileWayRadios.forEach((radio) => {
            radio.checked = radio.value === way;
        });

        const mobileStoreName = mobileFacet.querySelector('[data-facet-current-store]');
        const mobileYourStoreName = mobileFacet.querySelector('.your-store-name');
        if (mobileStoreName && store) mobileStoreName.textContent = store;
        if (mobileYourStoreName && store) mobileYourStoreName.textContent = store;

        const mobileRadios = mobileFacet.querySelectorAll('.facet-way__radio');
        mobileRadios.forEach((radio) => {
            radio.classList.remove('active-option');
            if (radio.dataset.storeAvailability === availability)
                radio.classList.add('active-option');
        });

        if (mobileFacet.syncAvailability) {
            mobileFacet.syncAvailability();
        } else {
            this.updateMobileYourStoreCounts();
        }
    }

    updateMobileYourStoreCounts() {
        const counts = p80.facetAvailabilityCounts || {};
        const mobileFacet = this.querySelector('your-store-facet-cio.facet-store-selector--mobile');
        if (!mobileFacet) return;

        const selectedStore = localStorage.getItem('storeLocation') || null;
        const pickupCounts = counts['facet-pickup'] || {};
        const viewCounts = counts['facet-view'] || {};
        const shopAllCount = counts['facet-shop-all'] || 0;
        const shipToYouCount = counts['facet-ship-to-you'] || 0;
        const pickupCount = selectedStore ? pickupCounts[selectedStore] || 0 : 0;
        const viewCount = selectedStore ? viewCounts[selectedStore] || 0 : 0;

        const setCount = (selector, value) => {
            const el = mobileFacet.querySelector(selector);
            if (el) el.textContent = '(' + value + ')';
        };

        const way = localStorage.getItem('storeWay') || 'all';
        if (way === 'pickup' && selectedStore) {
            setCount('[data-facet-your-store]', pickupCount);
        } else if (way === 'view' && selectedStore) {
            setCount('[data-facet-your-store]', viewCount);
        } else {
            setCount('[data-facet-your-store]', shopAllCount);
        }

        setCount('[data-facet-shop-all]', shopAllCount);
        setCount('[data-facet-ship-to-you]', shipToYouCount);
        setCount('[data-facet-pickup]', pickupCount);
        setCount('[data-facet-view]', viewCount);

        const loader = mobileFacet.querySelector('.facet-store-selector__loader');
        if (loader) setTimeout(() => loader.classList.remove('visible'), 500);
    }
}

/* ═══════════════════════════════════════════════════════════════
   REGISTER CUSTOM ELEMENT
   ═══════════════════════════════════════════════════════════════ */
if (!customElements.get('constructor-collection-browser')) {
    customElements.define('constructor-collection-browser', ConstructorCollectionBrowser);
}